import { t } from "@travily/i18n";
import { ChevronDownIcon } from "@ui/components/Icon/ChevronDownIcon";
import type { SectionJumpSection } from "@ui/components/SectionJumpNav";
import { tv } from "@ui/lib/tv";
import { Button } from "@ui/primitives/Button";
import { Menu, MenuItem, MenuPopover, MenuTrigger } from "@ui/primitives/Menu";

const sectionJumpBar = tv({
  slots: {
    root: "flex items-center justify-between gap-6 rounded-2xl bg-white px-5 py-3 shadow-md",
    text: "flex min-w-0 flex-col",
    title: "truncate font-semibold text-neutral-900 text-xl",
    subtitle: "truncate text-base text-neutral-600",
    trigger: [
      "flex shrink-0 cursor-pointer items-center gap-1 rounded-lg font-semibold text-base text-neutral-600",
      "outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 focus-visible:ring-offset-1",
    ],
    popover: "rounded-xl bg-white p-2 shadow-md",
    menu: "flex flex-col gap-1 outline-none",
    item: [
      "cursor-pointer rounded-lg px-2 py-1 text-lg text-neutral-600 outline-none",
      "data-[focused]:bg-neutral-50",
      "data-[selected]:bg-green-100 data-[selected]:font-semibold data-[selected]:text-green-800",
      // Explicit compound so the focused tint never overrides the selected one.
      "data-[selected]:data-[focused]:bg-green-100",
    ],
  },
});

export interface SectionJumpBarProps {
  /** Page title repeated in the bar (rendered as a `<p>` — the page `<h1>` stays the heading). */
  title: string;
  /** Secondary context line under the title. */
  subtitle?: string;
  sections: readonly SectionJumpSection[];
  /** Key of the section currently in view; names the menu trigger + checks the menu item. */
  activeKey: string | null;
  /** Called with a section's key when the reader asks to jump to it. */
  onSelect: (key: string) => void;
  /** Accessible name for the nav landmark; defaults to the localized "Page sections". */
  "aria-label"?: string;
  className?: string;
}

/**
 * Desktop floating companion bar for long section-based pages: page title and
 * context on the left, a current-section menu on the right that jumps between
 * the stacked sections. Selection renders as `menuitemradio`/`aria-checked`
 * (RAC single selection); jumping is the consumer's job — pair `activeKey`/
 * `onSelect` with `useScrollSpy`.
 *
 * Positioning (fixed/sticky, show-on-scroll) is owned by the page: the bar is
 * just the card.
 */
export function SectionJumpBar({
  title,
  subtitle,
  sections,
  activeKey,
  onSelect,
  className,
  "aria-label": ariaLabel,
}: SectionJumpBarProps) {
  const styles = sectionJumpBar();
  const activeLabel = sections.find(({ key }) => key === activeKey)?.label;

  return (
    <nav aria-label={ariaLabel ?? t("components.section_jump_bar.nav_label")} className={styles.root({ className })}>
      <div className={styles.text()}>
        <p className={styles.title()}>{title}</p>
        {subtitle && <p className={styles.subtitle()}>{subtitle}</p>}
      </div>
      <MenuTrigger>
        <Button aria-label={t("components.section_jump_bar.jump_to_section")} className={styles.trigger()}>
          {activeLabel}
          <ChevronDownIcon className="size-4" />
        </Button>
        <MenuPopover className={styles.popover()} placement="bottom end">
          <Menu className={styles.menu()} selectedKeys={activeKey ? [activeKey] : []} selectionMode="single">
            {sections.map(({ key, label }) => (
              <MenuItem className={styles.item()} id={key} key={key} onAction={() => onSelect(key)}>
                {label}
              </MenuItem>
            ))}
          </Menu>
        </MenuPopover>
      </MenuTrigger>
    </nav>
  );
}
